import Vue from 'vue'
import VueRouter from 'vue-router'
import { env } from '@/config'
import store from '@/store'
import { getTokenPayload } from '@/resources'
import BaseResource from '@/resources'

Vue.use(VueRouter)

// Detect locale from the URL path prefix (e.g. /fr/account → 'fr')
var LOCALE_CODES = ['fr', 'es', 'de']
var _firstSegment = window.location.pathname.split('/')[1]
var activeLocale = LOCALE_CODES.includes(_firstSegment) ? _firstSegment : 'en'

// Async component factories with explicit per-locale branches so webpack can
// statically analyze each import path (variable-only paths break the bundler).
function comp(name) {
    return function () {
        if (activeLocale === 'fr')
            return import('../locales/fr/components/' + name).catch(function () {
                return import('../components/' + name)
            })
        if (activeLocale === 'es')
            return import('../locales/es/components/' + name).catch(function () {
                return import('../components/' + name)
            })
        if (activeLocale === 'de')
            return import('../locales/de/components/' + name).catch(function () {
                return import('../components/' + name)
            })
        return import('../components/' + name)
    }
}

function view(name) {
    return function () {
        if (activeLocale === 'fr')
            return import('../locales/fr/views/' + name).catch(function () {
                return import('../views/' + name)
            })
        if (activeLocale === 'es')
            return import('../locales/es/views/' + name).catch(function () {
                return import('../views/' + name)
            })
        if (activeLocale === 'de')
            return import('../locales/de/views/' + name).catch(function () {
                return import('../views/' + name)
            })
        return import('../views/' + name)
    }
}

function accountView(name) {
    return function () {
        if (activeLocale === 'fr')
            return import('../locales/fr/views/account/' + name).catch(function () {
                return import('../views/account/' + name)
            })
        if (activeLocale === 'es')
            return import('../locales/es/views/account/' + name).catch(function () {
                return import('../views/account/' + name)
            })
        if (activeLocale === 'de')
            return import('../locales/de/views/account/' + name).catch(function () {
                return import('../views/account/' + name)
            })
        return import('../views/account/' + name)
    }
}

var Nav = comp('Nav.vue')
var Footer = comp('Footer.vue')

var routes = [
    {
        path: '/',
        alias: '/dashboard',
        name: 'dashboard',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('Grid.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Grid',
        },
    },
    {
        path: '/account',
        alias: '/you',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('Account.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'You',
        },
        children: [
            { path: '', redirect: { name: 'accountProfile' } },
            { path: 'profile', name: 'accountProfile', component: accountView('AccountProfile.vue'), meta: { title: 'Profile', loginRequired: true } },
            { path: 'payment', name: 'accountPayment', component: accountView('AccountPayment.vue'), meta: { title: 'Payment', loginRequired: true } },
            { path: 'subscription', name: 'accountSubscription', component: accountView('AccountSubscription.vue'), meta: { title: 'Subscription', loginRequired: true } },
            { path: 'notifications', name: 'accountNotifications', component: accountView('AccountNotifications.vue'), meta: { title: 'Notifications', loginRequired: true } },
            { path: 'reservations', name: 'accountReservations', component: accountView('AccountReservations.vue'), meta: { title: 'Reservations', loginRequired: true } },
            { path: 'waivers', name: 'accountWaivers', component: accountView('AccountWaivers.vue'), meta: { title: 'Waivers', loginRequired: true } },
            { path: 'lap-times', name: 'accountLapTimes', component: accountView('AccountLapTimes.vue'), meta: { title: 'Lap Times', loginRequired: true } },
            { path: 'teams', name: 'accountTeams', component: accountView('AccountTeams.vue'), meta: { title: 'Teams', loginRequired: true } },
            { path: 'presets/races/:id?', name: 'racesPresets', component: accountView('PresetsRaces.vue'), meta: { title: 'Race Presets', loginRequired: true } },
            { path: 'transactions', name: 'accountTransactions', component: accountView('AccountTransactions.vue'), meta: { title: 'Transactions', loginRequired: true } },
        ],
    },
    {
        path: '/subscription',
        name: 'subscription',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('Subscription.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Sim Racing Subscription',
        },
    },
    {
        path: '/races',
        name: 'races',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('Races.vue'),
        },
        children: [
            {
                path: 'presets/:id?',
                redirect: (to) => ({ name: 'racesPresets', params: to.params }),
            },
        ],
    },
    {
        path: '/laps',
        name: 'laps',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('MyLaps.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'MyLaps',
        },
    },
    {
        path: '/availability',
        name: 'availability',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('Availability.vue'),
        },
        children: [
            {
                path: 'cars',
                name: 'availabilityCars',
                component: view('AvailabilityCars.vue'),
                meta: {
                    title: 'Available Cars',
                },
            },
            {
                path: 'tracks',
                name: 'availabilityTracks',
                component: view('AvailabilityTracks.vue'),
                meta: {
                    title: 'Available Tracks',
                },
            },
        ],
    },
    {
        path: '/leaderboards/:id?',
        alias: '/boards/:id?',
        name: 'leaderboards',
        components: {
            nav: Nav,
            body: view('Boards.vue'),
        },
        meta: {
            title: 'Boards',
        },
    },
    {
        path: '/welcome',
        name: 'welcome',
        components: {
            body: view('WelcomeScreen.vue'),
        },
        meta: {
            bodyClass: 'f1-rear-opaque',
            forceDark: true,
        },
    },
    {
        path: '/menu/:locationSuffix?',
        name: 'menu',
        components: {
            body: view('MenuScreen.vue'),
        },
        meta: {
            bodyClass: 'overflow-hidden',
            forceDark: true,
        },
    },
    {
        path: '/splash',
        name: 'splash',
        components: {
            body: view('SplashScreen.vue'),
        },
        meta: {
            bodyClass: 'f1-rear',
        },
    },
    {
        path: '/display/:boardId?',
        name: 'display',
        components: {
            body: view('DisplayScreen.vue'),
        },
        meta: {
            bodyClass: 'f1-rear-opaque',
            forceDark: true,
        },
    },
    {
        path: '/pages/:id',
        name: 'customPageView',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('PageView.vue'),
        },
    },
    {
        path: '/reservations',
        name: 'reservations',
        components: {
            nav: Nav,
            body: view('Reservations.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Reservations',
        },
    },
    {
        path: '/reservations/all',
        name: 'reservationsAdvanced',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('ReservationsStart.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Reservations',
        },
    },
    {
        path: '/reservations/seating/groups/:id',
        name: 'seatingGroup',
        components: {
            nav: Nav,
            body: view('SeatingGroup.vue'),
        },
        beforeEnter(to, from, next) {
            if (to.query.keepToken !== 'true') {
                store.commit('logout')
            }
            next()
        },
        meta: {
            loginRequired: true,
            title: 'Your Session',
        },
    },
    {
        path: '/reservations/:calendarId',
        name: 'reservationsSingle',
        components: {
            nav: Nav,
            body: view('ReservationsSingle.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Reservations',
        },
    },
    {
        path: '/deals',
        name: 'deals',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('Deals.vue'),
        },
        meta: {
            title: 'Deals',
        },
    },
    {
        path: '/launcher/verify',
        name: 'launcherVerify',
        components: {
            nav: Nav,
            body: view('LauncherVerify.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Verify',
        },
    },
    {
        path: '/control/:calendarId',
        name: 'calendarControl',
        components: {
            nav: Nav,
            body: view('CalendarControl.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Race Control',
        },
    },
    {
        path: '/control/:calendarId/waiting',
        name: 'calendarControlWaitingRoom',
        components: {
            nav: Nav,
            body: view('CalendarControlWaitingRoom.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Waiting Room',
        },
    },
    {
        path: '/profile',
        name: 'profile',
        components: {
            nav: Nav,
            footer: Footer,
            body: view('Profile.vue'),
        },
        meta: {
            loginRequired: true,
        },
    },
    {
        path: '/subscription/add',
        name: 'addSubscription',
        components: {
            nav: Nav,
            footer: Footer,
        },
        meta: {
            loginRequired: true,
            title: 'Sim Racing Subscription',
        },
    },
    {
        path: '/vouchers/purchase',
        name: 'purchaseVoucher',
        components: {
            nav: Nav,
            body: view('VoucherPurchase.vue'),
        },
        meta: {
            bodyClass: 'porsche background-fixed',
            loginRequired: true,
            title: 'Sim Racing Vouchers',
        },
    },
    {
        path: '/vouchers/balance',
        name: 'voucherBalance',
        components: {
            nav: Nav,
            body: view('VoucherBalance.vue'),
        },
        meta: {
            bodyClass: 'porsche background-fixed',
            title: 'Voucher Balance',
        },
    },
    {
        path: '/login',
        name: 'login',
        components: {
            nav: Nav,
            body: view('Login.vue'),
        },
        async beforeEnter(to, from, next) {
            store.commit('logout')

            if (Object.keys(to.query).length > 0 && to.query.token) {
                let tokenPayload = getTokenPayload(to.query.token)
                let expireDate = new Date(tokenPayload.exp * 1000)
                if (expireDate > new Date()) {
                    store.commit('logout')
                    BaseResource.client.token = to.query.token
                    store.commit('setToken', to.query.token)

                    await store.dispatch('appInitialize')

                    if (to.query.redirect) {
                        next(to.query.redirect)
                    } else {
                        next({ name: 'change_password' })
                    }
                } else {
                    next({ name: 'login', query: { errorCode: 'tokenExpired' } })
                }
            } else {
                next()
            }
        },
        meta: {
            title: 'Log In',
        },
    },
    {
        path: '/logout',
        name: 'logout',
        components: {},
        beforeEnter(to, from, next) {
            store.commit('logout')
            next({ name: 'signup' })
        },
    },
    {
        path: '/login/password/reset',
        name: 'forgot_password',
        components: {
            nav: Nav,
            body: view('ForgotPassword.vue'),
        },
        beforeEnter(to, from, next) {
            store.commit('logout')
            next()
        },
    },
    {
        path: '/profile/password/change',
        name: 'change_password',
        components: {
            body: view('ChangePassword.vue'),
        },
        meta: {
            loginRequired: true,
            title: 'Change Password',
        },
    },
    {
        path: '/signup',
        name: 'signup',
        components: {
            nav: Nav,
            body: view('Signup.vue'),
        },
        beforeEnter(to, from, next) {
            if (to.query.keepToken !== 'true') {
                store.commit('logout')
            }
            next()
        },
        meta: {
            title: 'Sign Up',
        },
    },
    {
        path: '/admin/seats',
        name: 'adminCalendars',
        components: {
            nav: Nav,
            body: view('AdminMoved.vue'),
        },
        meta: {
            loginRequired: true,
            permsRequired: ['reservations.change_reservation'],
            title: 'Daily Planner',
        },
    },
    {
        path: '/admin/user/:userId',
        name: 'adminUser',
        components: {
            body: view('AdminUser.vue'),
        },
        meta: {
            loginRequired: true,
            permsRequired: ['reservations.change_reservation', 'user.change_user'],
        },
    },
    {
        path: '/admin/launcher',
        redirect: { name: 'adminRaceControl' },
    },
    {
        path: '/admin/race_control',
        name: 'adminRaceControl',
        components: {
            nav: Nav,
            body: view('AdminMoved.vue'),
        },
        meta: {
            loginRequired: true,
            permsRequired: ['leaderboards.can_manage_race_events'],
            title: 'Race Control',
        },
    },
    // 404
    {
        path: '*',
        name: 'PageNotFound',
        components: {
            nav: Nav,
            body: view('PageNotFound.vue'),
        },
        meta: {
            bodyClass: 'background-fixed',
        },
    },
]

const router = new VueRouter({
    mode: 'history',
    base: activeLocale !== 'en' ? '/' + activeLocale : process.env.BASE_URL || '/',
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
    routes,
})

router.beforeEach((to, from, next) => {
    if (!store.getters.token && to.meta.loginRequired) {
        return next({ name: 'signup', query: { redirect: to.fullPath } })
    }

    if (to.meta.staffRequired && !store.getters.isStaff) {
        return next({ name: 'login' })
    }

    if (to.meta.permsRequired) {
        const { permissions } = store.getters.permissions
        for (let perm of to.meta.permsRequired) {
            if (!permissions.includes(perm)) {
                return next({ name: 'login' })
            }
        }
    }

    if (to.meta.bodyClass) {
        document.getElementsByTagName('body')[0].className = to.meta.bodyClass
    } else {
        document.getElementsByTagName('body')[0].className = ''
    }

    if (to.meta.forceDark) {
        document.documentElement.setAttribute('data-theme', 'dark')
    } else if (from.meta.forceDark) {
        const brandingTheme = String(store.state.branding.design_theme || '').toUpperCase() === 'DARK' ? 'dark' : 'light'
        document.documentElement.setAttribute('data-theme', brandingTheme)
    }

    next()
})

router.afterEach((to, from) => {
    // Not sure why hash "anchors" aren't working -- and I even checked scrollBehavior on the router
    // Using this workaround for now
    if (to.hash) {
        let hashDOMId = to.hash.replace('#', '')
        let foundEl = null
        let timeoutMs = 1000
        let intervalMs = 50
        let currentMs = 0
        let timeoutId = setInterval(() => {
            currentMs += intervalMs
            foundEl = document.getElementById(hashDOMId)

            if (currentMs > timeoutMs || foundEl) {
                clearInterval(timeoutId)
            }

            if (foundEl) {
                foundEl.scrollIntoView({ behavior: 'smooth' })
            }
        }, intervalMs)
    }

    let companyName = env.COMPANY_NAME ? env.COMPANY_NAME : 'ShiftOS'

    if (store.getters.userResource?.attributes.location_company_name) {
        companyName = store.getters.userResource.attributes.location_company_name
    }

    if (to.meta.title) {
        document.title = `${to.meta.title} | ${companyName}`
    } else {
        document.title = companyName
    }
})

export default router
